import React, { useState, useContext, useEffect } from 'react';
import CanvasContext from '../../../context/CanvasContext';
import styles from './styles/UploadModal.module.css';
import MediaPlayer from '../../../utils/MediaPlayer';
import { getMediaSrc } from '../../../utils/utils';

const UploadModal = ({ uploadType, setIsModalOpen }) => {
    const {
        updateSituationAndNode,
        updateGroup,
        selectedSituation,
        setSelectedGroup,
        selectedGroup,
        loading
    } = useContext(CanvasContext);

    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        let existingMedia = null;
        if (selectedSituation) {
            existingMedia = uploadType === 'image' ? selectedSituation.image : selectedSituation.audio;
        } else if (selectedGroup) {
             existingMedia = uploadType === 'image' ? selectedGroup.image : selectedGroup.audio;
        }

        setPreview(existingMedia || null);

        setFile(null);
        setErrorMessage('');

    }, [selectedSituation, selectedGroup, uploadType]);

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        setErrorMessage('');
        setFile(null);
        setPreview(null);

        if (file) {
            
            const maxSize = 50 * 1024 * 1024;
            if (file.size > maxSize) {
                setErrorMessage(`File size exceeds ${maxSize / 1024 / 1024}MB limit.`);
                return;
            }
            const isImageUpload = uploadType === 'image';
            if (isImageUpload && !file.type.startsWith('image/')) {
                 setErrorMessage('Invalid file type. Please upload an image.');
                 return;
            }
             if (!isImageUpload && !file.type.startsWith('audio/') && !file.type.startsWith('video/')) {
                 setErrorMessage('Invalid file type. Please upload an audio or video file.');
                 return;
             }


            setFile(file);

            const reader = new FileReader();
            reader.onloadend = () => setPreview(reader.result);
            reader.readAsDataURL(file); 
        }
    };

    const handleSave = async () => {
        
        if (!file || loading || (!selectedSituation?._id && !selectedGroup)) {
            if(!file) setErrorMessage("No file selected to save.");
            if(loading) setErrorMessage("An operation is already in progress.");
            if(!selectedSituation?._id && !selectedGroup) setErrorMessage("No situation or group selected.");
            return;
        }

        const formData = new FormData();
        formData.append(uploadType === 'image' ? 'image' : 'audio', file);

        try {
            setErrorMessage('');

            if (selectedSituation?._id) {
                await updateSituationAndNode(selectedSituation._id, formData);
            } else if (selectedGroup) {
                
                const updatedGroup = await updateGroup(formData);
                setSelectedGroup(updatedGroup);
            }

            setIsModalOpen(false);

        } catch (error) {
            console.error(`Error updating ${uploadType}: `, error);
            const backendError = error.response?.data?.message || 'An error occurred while uploading.';
            setErrorMessage(backendError + " Please try again.");
        }
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setFile(null);
        setPreview(null);
        setErrorMessage('');
    };

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
                <h2 className={styles.modalTitle}>
                    Upload {uploadType === 'image' ? 'Image' : 'Audio/Video'}
                </h2>
                {/* Input trigger button */}
                 <label
                    htmlFor="file-upload-input"
                    className={`${styles.buttonBase} ${styles.uploadButton} ${loading ? styles.disabled : ''}`}
                >
                    {preview ? 'Change File' : 'Select File'}
                </label>
                <input
                    type="file"
                    id="file-upload-input"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                    accept={uploadType === 'image' ? 'image/*' : 'audio/*,video/*'}
                    disabled={loading}
                />

                {/* Error Message Display */}
                {errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}

                {/* Preview Area */}
                {preview && (
                    <div className={styles.previewContainer}>
                        {uploadType === 'image' ? (
                            <img src={preview} alt="Preview" className={styles.previewImage} />
                        ) : (
                            <MediaPlayer
                                src={getMediaSrc(preview)}
                                autoplay={false}
                                key={getMediaSrc(preview)}
                            />
                        )}
                         {file && <p className={styles.fileName}>Selected: {file.name}</p>} {/* Show selected file name */}
                    </div>
                )}


                {/* Action Buttons */}
                <div className={styles.buttonGroup}>
                    <button
                        onClick={handleSave}
                        className={`${styles.buttonBase} ${styles.saveButton}`}
                        
                        disabled={!file || loading}
                    >
                        {loading ? 'Saving...' : 'Save'} {/* Indicate loading */}
                    </button>
                    <button
                        onClick={closeModal}
                        className={`${styles.buttonBase} ${styles.cancelButton}`}
                        disabled={loading} 
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UploadModal;